import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { KeyRound, ShieldCheck, Trash2, UserPlus } from 'lucide-react';
import { useState } from 'react';

import { RoleGate } from '@/components/RoleGate';
import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { CreateWorkspaceUserModal } from '@/features/workspaces/components/CreateWorkspaceUserModal';
import { EditMemberAccessModal } from '@/features/workspaces/components/EditMemberAccessModal';
import { InviteMemberModal } from '@/features/workspaces/components/InviteMemberModal';
import { useRemoveMember } from '@/features/workspaces/hooks/useRemoveMember';
import type { AccessScope } from '@/features/workspaces/hooks/useUpdateMemberAccess';
import { useUpdateMemberRole } from '@/features/workspaces/hooks/useUpdateMemberRole';
import { useAuth } from '@/lib/auth/AuthProvider';
import {
  type WorkspaceRole,
  getWorkspaceBySlugQuery,
  listMembersQuery,
} from '@/lib/supabase/queries/workspaces';

export const Route = createFileRoute('/_app/$wsSlug/settings/members')({
  component: MembersSettingsPage,
});

// Flujo unico para sumar gente al workspace es "Crear usuario". El boton
// "Invitar miembro" (flujo via email) queda oculto pero su codigo se conserva
// para reactivarlo facil cuando se arregle el bug de send-invite (500 cuando
// el email ya existe en auth.users).
const SHOW_INVITE_BUTTON = false; // TODO: reactivar cuando send-invite este fixeado

const ROLE_LABELS: Record<WorkspaceRole, string> = {
  owner: 'Propietario',
  admin: 'Administrador',
  member: 'Miembro',
  guest: 'Invitado',
};

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('es', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function MembersSettingsPage() {
  const { wsSlug } = Route.useParams();
  const { user } = useAuth();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [accessEdit, setAccessEdit] = useState<{
    userId: string;
    userName: string;
    scope: AccessScope;
  } | null>(null);

  const { data: workspace } = useQuery(getWorkspaceBySlugQuery(wsSlug));
  const workspaceId = workspace?.id ?? '';

  const { data: members = [], isLoading } = useQuery({
    ...listMembersQuery(workspaceId),
    enabled: !!workspaceId,
  });

  const updateRole = useUpdateMemberRole(workspaceId);
  const removeMember = useRemoveMember(workspaceId);

  // El caller solo puede editar acceso de otros si tiene rol owner o admin.
  const myMembership = members.find((m) => m.user_id === user?.id);
  const canEditAccess = myMembership?.role === 'owner' || myMembership?.role === 'admin';

  if (isLoading || !workspaceId) {
    return (
      <div className="flex items-center justify-center py-12 text-quiet text-sm">Cargando...</div>
    );
  }

  return (
    <RoleGate wsSlug={wsSlug}>
      <div className="max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-base font-semibold text-ink">Miembros</h2>
            <p className="text-sm text-quiet">
              Miembros del workspace y sus roles. Crea usuarios nuevos o quita acceso. Owner / Admin
              pueden gestionar; Member trabaja; Guest solo lee.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {canEditAccess ? (
              <Button
                size="sm"
                className="whitespace-nowrap"
                onClick={() => setCreateUserOpen(true)}
                title="Crear usuario con contraseña directa, sin email de invitación"
              >
                <KeyRound size={14} />
                Crear usuario
              </Button>
            ) : null}
            {SHOW_INVITE_BUTTON && (
              <Button variant="ghost" onClick={() => setInviteOpen(true)} size="sm">
                <UserPlus size={14} />
                Invitar miembro
              </Button>
            )}
          </div>
        </div>

        {/* Members list */}
        <div className="flex flex-col divide-y divide-border-soft border border-border rounded-lg overflow-hidden">
          {members.map((member) => {
            const profile = member.profile;
            const name = profile?.display_name ?? profile?.email ?? 'Usuario';
            const email = profile?.email ?? '';
            const isCurrentUser = member.user_id === user?.id;
            const isOwner = member.role === 'owner';

            return (
              <div
                key={member.user_id}
                className="flex items-center gap-3 px-4 py-3 bg-surface hover:bg-surface-elev transition-colors"
              >
                {/* Avatar */}
                <Avatar name={name} src={profile?.avatar_url} size="sm" />

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-sm font-medium text-ink truncate">{name}</span>
                    {isCurrentUser && (
                      <span className="text-[10px] bg-accent-sf text-accent rounded px-1.5 py-0.5 font-medium">
                        Tu
                      </span>
                    )}
                    {isCurrentUser && member.hidden_from_pickers ? (
                      <span
                        className="text-[10px] bg-surface-elev border border-border-soft text-quiet rounded px-1.5 py-0.5 font-medium"
                        title="Solo tú te ves en esta lista. El resto del workspace no te ve aquí."
                      >
                        🥷 Modo Ninja
                      </span>
                    ) : null}
                  </div>
                  <span className="text-xs text-quiet truncate">{email}</span>
                </div>

                {/* Joined at */}
                <span className="text-xs text-mute shrink-0 hidden sm:block">
                  {formatDate(member.joined_at)}
                </span>

                {/* Access scope badge — visible cuando el miembro tiene acceso
                  limitado, para que sea evidente quién está restringido. */}
                {member.access_scope === 'limited' ? (
                  <span
                    className="hidden md:inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-warning/10 text-[color:var(--warning)] border border-[color:var(--warning)]/30"
                    title="Acceso limitado a algunos proyectos"
                  >
                    <ShieldCheck size={10} />
                    Limitado
                  </span>
                ) : null}

                {/* Role selector */}
                {isOwner ? (
                  <span className="text-xs font-medium text-quiet px-2 py-1 border border-border rounded-md">
                    {ROLE_LABELS.owner}
                  </span>
                ) : (
                  <select
                    value={member.role}
                    onChange={(e) => {
                      void updateRole.mutate({
                        userId: member.user_id,
                        role: e.target.value as WorkspaceRole,
                      });
                    }}
                    className="text-xs h-7 rounded-md border border-border bg-surface px-2 outline-none focus:border-accent transition-colors"
                    aria-label={`Rol de ${name}`}
                    disabled={updateRole.isPending}
                  >
                    <option value="admin">{ROLE_LABELS.admin}</option>
                    <option value="member">{ROLE_LABELS.member}</option>
                    <option value="guest">{ROLE_LABELS.guest}</option>
                  </select>
                )}

                {/* Editar acceso por proyecto. Habilitado solo para owner/admin
                  del workspace y nunca para el owner (su acceso es siempre
                  global por definición — la RPC también lo dejaría editar
                  pero no tiene sentido). */}
                {canEditAccess && !isOwner && (
                  <button
                    type="button"
                    title="Editar acceso a proyectos"
                    aria-label="Editar acceso a proyectos"
                    onClick={() =>
                      setAccessEdit({
                        userId: member.user_id,
                        userName: name,
                        scope: (member.access_scope as AccessScope | null) ?? 'all',
                      })
                    }
                    className="text-mute hover:text-accent transition-colors p-1 rounded"
                  >
                    <ShieldCheck size={14} />
                  </button>
                )}

                {/* Remove */}
                {!isOwner && !isCurrentUser && (
                  <button
                    type="button"
                    title="Eliminar miembro"
                    aria-label="Eliminar miembro"
                    onClick={() => {
                      if (confirm(`¿Eliminar a ${name} del workspace?`)) {
                        void removeMember.mutate(member.user_id);
                      }
                    }}
                    disabled={removeMember.isPending}
                    className="text-mute hover:text-critical transition-colors p-1 rounded"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            );
          })}

          {members.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-quiet">Sin miembros todavia.</div>
          )}
        </div>

        {/* Invite Modal — montado solo cuando el flujo de invitacion por email
          esta habilitado (SHOW_INVITE_BUTTON). Codigo conservado para reactivar
          facil cuando se arregle el bug de send-invite. */}
        {SHOW_INVITE_BUTTON && (
          <InviteMemberModal
            open={inviteOpen}
            onOpenChange={setInviteOpen}
            workspaceId={workspaceId}
          />
        )}

        {/* Crear Usuario Modal — solo visible para owners/admins (botón
          gateado en el header), pero igual lo dejamos detrás de la
          condicional `canEditAccess` para defensa en profundidad. */}
        {canEditAccess ? (
          <CreateWorkspaceUserModal
            open={createUserOpen}
            onOpenChange={setCreateUserOpen}
            workspaceId={workspaceId}
          />
        ) : null}

        {/* Edit Access Modal — open cuando hay un target seleccionado. Se
          desmonta al cerrar para que useEffect del modal resetee el form. */}
        {accessEdit && (
          <EditMemberAccessModal
            open={!!accessEdit}
            onOpenChange={(v) => {
              if (!v) setAccessEdit(null);
            }}
            workspaceId={workspaceId}
            userId={accessEdit.userId}
            userName={accessEdit.userName}
            currentScope={accessEdit.scope}
          />
        )}
      </div>
    </RoleGate>
  );
}
